import React, { useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";


// import { RootStoreContext } from "../stores/root_store";
// import { MessageProps } from "../stores/messages_store";
import { AuthContext } from "../contexts/auth_context";
import useAuth from "hooks/useAuth";
import useRealtime from "hooks/useRealtime";
import useUserIdentity from "hooks/useUserIdentity";
import useRoles from "hooks/useRoles";
// import { TransportLayerUpdateType } from "stores/root_store";

import { RealtimePacketType } from 'contexts/realtime_context';

import socket from 'utils/socket';

// https://realtime-tuna.fancyhands.com

// if (import.meta.env.REALTIME_URL) {
// 	REALTIME_URL = import.meta.env.REALTIME_URL;
// }


type SocketEmitEventType = "send" | "identify" | "join"

export interface SocketContextType {
    socketId: string | null;
	sendData: (data: object) => void;
	socketEmit: (event: SocketEmitEventType, data: object) => void;
    connectionState: string;
    connectionRetryDelay: number;
}

export const SocketContext = React.createContext<SocketContextType>({
    socketId: null,
    sendData: () => { throw 'Not implemented: sendData' },
    socketEmit: () => { throw 'Not implemented: socketEmit' },
    connectionState: 'not initialized',
    connectionRetryDelay: 0
});


function _SocketProvider ({ children } : { children: JSX.Element[] | JSX.Element }) {

	const { token } = React.useContext(AuthContext);
    const { currentUser } = useAuth();
    const { rolesNames } = useRoles();
    const { forwardUpdate } = useRealtime();
    const { currentIdentity } = useUserIdentity();
    const [connectionState, setConnectionState] = React.useState('init');
    const [connectRetries, setConnectRetries] = React.useState(0);
    const [connectionRetryDelay, setConnectionRetryDelay] = React.useState(0);

    // console.log("SocketProvider token %s", token);


    const doConnect = useCallback(() => {

        if (!currentUser?.uuid) {
            // console.error("No currentUser, not connecting to socket.");
            setConnectionState('missing:user');
            return;
        }
        else {
            console.log("Connecting to socket with user %s", currentUser.uuid);
        }

        if (!token) {
            setConnectionState('missing:token');
            // console.error("no token, not connecting to socket.");
            return;
        }

        if (socket.io.opts.query) {
            console.log("Adding token", token, "to socket.io.opts.query");
            socket.io.opts.query.morty_token = token;
        }
        else {
            console.error("No query object on socket.io.opts");
        }
        console.log("Calling socket connect");
        socket.connect();

        socket.on('identified', () => {

            /* forward the message to the realtime contxt */
            socket.on("published", (event: RealtimePacketType) => {
                // console.log("Received published event %o", event);
                forwardUpdate(event as RealtimePacketType);
            });
        });

        socket.on("connect", () => {
            if (!currentUser?.uuid) {
                console.log("socket No current user, not identifying");
                return;
            }

            console.log("socket have current user, Connected to socket, re-identifying");
            socketEmit('identify', {
                user_uuid: currentUser.uuid,
                role: currentIdentity?.role
            });
            setConnectionState('connected');
            setConnectRetries(0);
            setConnectionRetryDelay(0);
            console.log("Connected socket: ", socket.connected);
        });

        socket.on("disconnect", () => {
            console.log("Socket disconnected, updating connectionState");
            // setConnectionState('disconnected');
        });


        socket.on("connect_error", () => {
            console.log("Socket connect error");
            setConnectionState('error');
        });

        return () => {
            console.log("SocketProvider cleanup");
            setConnectRetries(0);
            setConnectionRetryDelay(0);
            socket.disconnect();
        }

    }, [token, currentUser?.uuid, currentIdentity?.role]);

    // handle initial connection
    useEffect(() => {
        console.log("socket stuff...", connectionState);
		if(!token) {
            console.log("No token, not setting up socket");
			return;
		}
        else {
            console.log("Setting up socket with token %s", token);
        }

        if(!socket?.connected && !['connecting', 'connected'].includes(connectionState)) {
            console.log("socket connecting?", connectionState);
            setConnectionState('connecting');
		    doConnect();
        }

		return () => {
            if(connectionState != 'init') {
                console.log("SocketProvider cleanup");
                setConnectionState('disconnected');
            }
		}
	}, [token, currentUser?.uuid]);



    // // include the socket Id because we want to
    // // re-identity every time we get a new socket
    // useEffect(() => {
    //     console.log("Maybe attempt to identify:", connectionState);
    //     if(connectionState != 'connected') {
    //         console.log(`Not connected (${connectionState}), not identifying`);
    //         return;
    //     }
    //     if(!currentIdentity?.uuid) {
    //         console.log("No current identity, not identifying");
    //         return;
    //     }
    //     if(!socket.id) {
    //         console.log("No socket id, not identifying, socket: %o", socket);
    //         return;
    //     }
    //     console.log("Identifying with socket %o", socket.id);
    //     socketEmit('identify', {
    //         identity: currentIdentity.uuid,
    //         role: currentIdentity.role
    //     });
    // }, [currentIdentity?.uuid, currentIdentity?.role, connectionState]);

    useEffect(() => {
        console.log('connected socket.id', socket.id);
    }, [socket.id]);

    /* handle reconnect */
    useEffect(() => {
        let timer = null;
        if (['error'].includes(connectionState)) {
            console.log("Socket disconnected, retrying, ", connectionState, 'retries, ', connectRetries);

            const cnt = connectRetries;
            const delay = 1_000 * cnt;
            setConnectRetries(cnt + 1);
            timer = setTimeout(() => {
                setConnectionState('connecting');
                console.log("Socket not connected, reconnecting");
                doConnect();
            }, delay);
            console.log("socket Disconnected, trying to reconnect from useEffect, delay", delay);
            setConnectionState('disconnected');
            setConnectionRetryDelay(delay);
            // doConnect();
        }
        return () => {
            timer && clearTimeout(timer);
        }
    }, [connectionState]);

    useEffect(() => {
        if(rolesNames.includes('sudo')) {
            socketEmit('join',  { channel: 'sudo/identities'});
        }
        if(rolesNames.includes('support')) {
            socketEmit('join',  { channel: 'support/shift_history'});
        }
    }, [rolesNames]);


    useEffect(() => {
        if(currentIdentity?.role == 'assistant') {
            socketEmit('join',  { channel: 'request/announce'});
        }
    }, [currentIdentity?.role]);


    function sendData(data: object) {
        socket.emit("send", data);
    }

    function socketEmit(event: SocketEmitEventType, data: object) {
        // console.log("Emitting %s with data %o", event, data);
        socket.emit(event, data);
    }

    // function removeReceiver(recvFunc : ReceiverFunc) : void {
	// 	console.log("FIXME: Removing receiver %o", recvFunc);
	// 	addReceiver(knownReceivers.filter((recv) => recv !== recvFunc));
	// }
	// const SocketAddReceiver = React.createContext(addReceiver);
	// const SocketRemoveReceiver = React.createContext(removeReceiver);

    const data = {
        socketId: socket.id || null,
        sendData,
        socketEmit,
        connectionState,
        connectionRetryDelay
    }
	return (
        <SocketContext.Provider value={data}>
                    {children}
        </SocketContext.Provider>

	)


}

const SocketProvider = observer(_SocketProvider);
export { SocketProvider };
